import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Input, Button, message, Tag, Space, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import AuthContext from "../context/AuthProvider";
import axios from "../api/axios";
import Header from "../components/layouts/Header";

const { Text } = Typography;
const GET_ADDRESSES_URL  = '/admin/get-addresses';
const UPDATE_ADDRESS_URL = '/admin/update-address';

const USAGE_COLORS = {
    Transportation: 'blue',
    Warehouse: 'green',
    Other: 'default'
};

const AdminPage = () => {
    const { auth } = useContext(AuthContext);
    const navigate  = useNavigate();
    const [data, setData]           = useState([]);
    const [search, setSearch]       = useState('');
    const [editing, setEditing]     = useState({});
    const [saving, setSaving]       = useState(null);

    const loadAddresses = async () => {
        try {
            const res = await axios.post(GET_ADDRESSES_URL, { token: auth.accessToken });
            setData(res.data);
        } catch {
            navigate('/');
        }
    };

    useEffect(() => { loadAddresses(); }, []);

    const handleEdit = (ogUuid, currentAddress) => {
        setEditing(prev => ({ ...prev, [ogUuid]: currentAddress ?? '' }));
    };

    const handleChange = (ogUuid, value) => {
        setEditing(prev => ({ ...prev, [ogUuid]: value }));
    };

    const handleSave = async (ogUuid) => {
        setSaving(ogUuid);
        try {
            await axios.post(UPDATE_ADDRESS_URL, {
                token:   auth.accessToken,
                ogUuid:  ogUuid,
                address: editing[ogUuid]
            });
            message.success('Address updated');
            setEditing(prev => { const n = { ...prev }; delete n[ogUuid]; return n; });
            loadAddresses();
        } catch {
            message.error('Failed to update address');
        } finally {
            setSaving(null);
        }
    };

    const handleCancel = (ogUuid) => {
        setEditing(prev => { const n = { ...prev }; delete n[ogUuid]; return n; });
    };

    const filteredData = data.filter(row => {
        const q = search.toLowerCase();
        return (
            (row.OG_Number || '').toLowerCase().includes(q) ||
            (row.OG_Name   || '').toLowerCase().includes(q) ||
            (row.Address   || '').toLowerCase().includes(q) ||
            (row.DeviceUsage || '').toLowerCase().includes(q)
        );
    });

    const missingCount = data.filter(r => !r.Address).length;

    const columns = [
        {
            title: 'OG Number',
            dataIndex: 'OG_Number',
            key: 'OG_Number',
            width: 110,
            sorter: (a, b) => (a.OG_Number || '').localeCompare(b.OG_Number || ''),
            render: val => val
                ? <Text strong>{val}</Text>
                : <Text type="secondary">—</Text>
        },
        {
            title: 'OG Name',
            dataIndex: 'OG_Name',
            key: 'OG_Name',
            sorter: (a, b) => a.OG_Name.localeCompare(b.OG_Name)
        },
        {
            title: 'Type',
            dataIndex: 'DeviceUsage',
            key: 'DeviceUsage',
            width: 150,
            filters: [
                { text: 'Transportation', value: 'Transportation' },
                { text: 'Warehouse',      value: 'Warehouse'      },
                { text: 'Other',          value: 'Other'          }
            ],
            onFilter: (value, record) => record.DeviceUsage === value,
            render: val => <Tag color={USAGE_COLORS[val] || 'default'}>{val}</Tag>
        },
        {
            title: 'Address',
            dataIndex: 'Address',
            key: 'Address',
            render: (text, record) => {
                const isEditing = Object.prototype.hasOwnProperty.call(editing, record.OGUuid);
                if (isEditing) {
                    return (
                        <Input
                            value={editing[record.OGUuid]}
                            onChange={e => handleChange(record.OGUuid, e.target.value)}
                            placeholder="Enter address"
                            style={{ width: '100%' }}
                        />
                    );
                }
                return text
                    ? text
                    : <Tag color="red">Missing</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            render: (_, record) => {
                const isEditing = Object.prototype.hasOwnProperty.call(editing, record.OGUuid);
                if (isEditing) {
                    return (
                        <Space>
                            <Button
                                type="primary"
                                size="small"
                                loading={saving === record.OGUuid}
                                onClick={() => handleSave(record.OGUuid)}
                            >
                                Save
                            </Button>
                            <Button size="small" onClick={() => handleCancel(record.OGUuid)}>
                                Cancel
                            </Button>
                        </Space>
                    );
                }
                return (
                    <Button size="small" onClick={() => handleEdit(record.OGUuid, record.Address)}>
                        Edit
                    </Button>
                );
            }
        }
    ];

    return (
        <div className="app-container">
            <Header />
            <section style={{ padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Manage Addresses</h2>
                        <Text type="secondary">{data.length} total OGs{missingCount > 0 && <> · <Text type="danger">{missingCount} missing address</Text></>}</Text>
                    </div>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Search by OG number, name, address or type..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        allowClear
                        style={{ width: 360 }}
                    />
                </div>
                <Table
                    rowKey="OGUuid"
                    columns={columns}
                    dataSource={filteredData}
                    pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `${total} records` }}
                    size="small"
                    rowClassName={record => !record.Address ? 'row-missing-address' : ''}
                />
            </section>
            <style>{`.row-missing-address td { background: #fff7f7 !important; }`}</style>
        </div>
    );
};

export default AdminPage;

